/**
 * SEARCH — the SEO title, the meta description and the article's web address.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ WHY AN SEO PANE IS NOT A VIOLATION OF "NO SEO WORK ANYWHERE IN THEM"
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ADR-0007's rule governs what the two SPAs ARE: no server rendering, no crawlability, no metadata
 * for their own pages. This pane does none of that. It lets an author write metadata for a
 * **magazine page rendered by moonx.app** — a different, public system that legitimately does SEO —
 * and `01-system-overview.md:294` asks for exactly this, an *"SEO panel (slug until first
 * publish, `seo_title`, `meta_description`)"* in the editor row. The fields are real, autosaved and
 * versioned, and `magazine-api` consumes them. (`docs/ui/04-pages.md:470-492`, which states the
 * question correctly after a first reading that had it wrong.)
 *
 * ★ THE CAPS ARE THE CONTRACT'S, NOT THE PROTOTYPE'S. The prototype caps the title at 60 and the
 * description at 155; the contract allows **200** and **400**. Counting down from a number the
 * platform does not enforce trains an author to stop writing early for no reason.
 *
 * ★★ THE ADDRESS IS MADE FROM THE TITLE, AND THE AUTHOR NO LONGER CHOOSES IT (design 24, T11).
 * The teammate: "we must generate the URL ourselves from the article's title, with no options;
 * critical for SEO"; the owner agreed. Until then (register R-019, wave 25c) this pane let the author
 * type a candidate, check it with `checkArticleSlugAvailability` and put it on the draft, and the
 * next save RESERVED it for good. Now nothing here writes `slug`, so the autosave sends none
 * (./address.ts, `slugChange`), and the platform makes one from the title at first publication
 * (D28's chain: `mintAddress`, src/modules/articles/services/article-address.repository.ts).
 *
 * ★ WHAT IS LEFT IS ONE LINE, "Web address": made from the title on a draft; the address itself,
 * fixed, once the article is published. A draft that reserved an address before this change keeps
 * it - the platform mints only when the draft holds none - so that one is shown as reserved. The
 * availability check stays on the server, unused, to be retired later; the cabinet no longer asks
 * it, so its refusals (`SLUG_FROZEN` among them, decision-log row 219) have nowhere to land.
 *
 * ★ THE FOCUS KEYWORD IS NOT DRAWN. `docs/ui/04-pages.md:464` marks it PLATFORM WORK OWED —
 * *"`keyword` is 0 in the specification. Nothing stores it."*
 */
import { Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { countTextLength } from '../../../features/cabinet/api/editor-measure';
import { useTranslation } from '../../../i18n';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import type { EditorDraft } from './document';

/** `WorkingCopy.seoTitle`'s ceiling, as the contract sets it. Not the prototype's 60. */
const SEO_TITLE_MAX = 200;
/** `WorkingCopy.metaDescription`'s ceiling. Not the prototype's 155. */
const META_DESCRIPTION_MAX = 400;

/** A published article's address: said as a fact about the article, never as a field error. */
export function slugFrozenNote(t: ArticlesT = articlesT()): string {
  return t('editor.search.slugFrozen');
}

/*
 * A draft holding no address says the platform makes it at first publication; one that reserved an
 * address before design 24, T11 keeps it, and says so (the header).
 */

export interface SearchPanelProps {
  readonly draft: EditorDraft;
  readonly disabled: boolean;
  /** Ignored since design 24, T11: the address is made from the title, so nothing is checked. */
  readonly onCheckSlug?: ((slug: string) => Promise<boolean>) | undefined;
  readonly onChange: (change: (draft: EditorDraft) => EditorDraft) => void;
  /** The address the platform holds for this article - what the last accepted save left (R-019). */
  readonly savedSlug?: string | null | undefined;
  /** Ignored since design 24, T11: nothing sends an address now, so no save can refuse one. */
  readonly saveRefusal?: string | undefined;
  /**
   * ★ The article has been published (it is live, or taken down after), so its address is fixed -
   * said from the start (the owner's live walk of real cabinets, 2026-10-01). Before, the pane
   * learned it only from a check refused `SLUG_FROZEN`, so a published article - every one brought
   * over from the old apps - read "It becomes fixed when the article is first published" beside
   * greyed controls. Now it shows the address and says it is fixed.
   */
  readonly published?: boolean | undefined;
}

export function SearchPanel({
  draft,
  disabled,
  onChange,
  savedSlug,
  published = false,
}: SearchPanelProps) {
  const { t } = useTranslation('articles');
  /** The address the platform holds, if any - nothing on this pane changes it (the header). */
  const held = (savedSlug === undefined ? draft.slug : (savedSlug ?? '')).trim();
  const titleCp = countTextLength(draft.seoTitle);
  const descriptionCp = countTextLength(draft.metaDescription);

  return (
    <Stack component="section" gap="md" aria-labelledby="editor-search-title">
      <Stack gap={4}>
        <Title id="editor-search-title" order={2} size="h5">
          {t('editor.search.title')}
        </Title>
        <Text size="xs" c="dimmed">
          {t('editor.search.intro')}
        </Text>
      </Stack>

      <TextInput
        label={t('editor.search.seoTitle')}
        description={t('common.ofCharacters', { count: SEO_TITLE_MAX, value: String(titleCp) })}
        disabled={disabled}
        value={draft.seoTitle}
        error={titleCp > SEO_TITLE_MAX ? t('editor.search.tooLong') : null}
        onChange={(event) => {
          const seoTitle = event.currentTarget.value;
          onChange((current) => ({ ...current, seoTitle }));
        }}
      />

      <Textarea
        label={t('editor.search.metaDescription')}
        description={t('common.ofCharacters', {
          count: META_DESCRIPTION_MAX,
          value: String(descriptionCp),
        })}
        autosize
        minRows={2}
        disabled={disabled}
        value={draft.metaDescription}
        error={descriptionCp > META_DESCRIPTION_MAX ? t('editor.search.tooLong') : null}
        onChange={(event) => {
          const metaDescription = event.currentTarget.value;
          onChange((current) => ({ ...current, metaDescription }));
        }}
      />

      {/* ★ ONE LINE FOR THE ADDRESS (design 24, T11, the header): no field, no check, no choice. */}
      <Stack gap={2}>
        <Text size="sm" fw={700}>
          {t('editor.search.webAddress')}
        </Text>
        {held.length === 0 ? null : <Text size="sm">{held}</Text>}
        <Text size="xs" c="dimmed">
          {published
            ? slugFrozenNote(t)
            : held.length === 0
              ? t('editor.search.madeFromTitle')
              : t('editor.search.reserved')}
        </Text>
      </Stack>
    </Stack>
  );
}
